import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  collection,
  onSnapshot,
  orderBy,
  query,
  Timestamp,
  where,
} from "firebase/firestore";

import { db } from "../../FirebaseConfig";
import { useAuth } from "../../lib/auth-context";
import { useCollection } from "../../lib/firestore-hooks";
import {
  dowEs,
  formatFecha,
  formatHora,
  todaySantiago,
} from "../../lib/format";
import type {
  AvionDoc,
  BlackoutDoc,
  VueloDoc,
} from "../../lib/types";

interface VueloRow extends VueloDoc {
  id: string;
}
interface AvionRow extends AvionDoc {
  id: string;
}
interface BlackoutWithAircraft extends BlackoutDoc {
  id: string;
  aircraftId: string;
}

/**
 * /admin — operations dashboard. Three sections, in priority order:
 *   A. Solicitudes pendientes — the admin's main daily action
 *   B. Hoy & Mañana — flights happening soon, at a glance
 *   C. Estado de aviones — where each plane is now + next flight +
 *      active/upcoming blackout
 *
 * Full unfiltered list lives at /admin/vuelos.
 */
export function AdminResumen() {
  const { usuario } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState<VueloRow[] | null>(null);
  const { rows: aviones } = useCollection<AvionDoc>("Aviones", orderBy("displayName"));
  const today = todaySantiago();

  useEffect(() => {
    const q = query(collection(db, "Vuelos"), orderBy("fecha"));
    const unsub = onSnapshot(q, (snap) => {
      const list: VueloRow[] = [];
      snap.forEach((d) => list.push({ id: d.id, ...(d.data() as VueloDoc) }));
      list.sort((a, b) => {
        if (a.fecha !== b.fecha) return a.fecha < b.fecha ? -1 : 1;
        return a.hora < b.hora ? -1 : a.hora > b.hora ? 1 : 0;
      });
      setRows(list);
    });
    return () => unsub();
  }, []);

  // Subscribe to all aircraft blackouts (one subscription per aircraft —
  // small set so simpler than a collection-group query). Filters to
  // active + upcoming ranges (endAt >= today).
  const [blackouts, setBlackouts] = useState<BlackoutWithAircraft[]>([]);
  useEffect(() => {
    if (!aviones || aviones.length === 0) return;
    const cutoff = Timestamp.fromDate(new Date(`${today}T00:00:00`));
    const unsubs: (() => void)[] = [];
    const byAircraft = new Map<string, BlackoutWithAircraft[]>();
    for (const a of aviones) {
      const q = query(
        collection(db, "Aviones", a.id, "Blackouts"),
        where("endAt", ">=", cutoff),
        orderBy("endAt", "asc")
      );
      const u = onSnapshot(q, (snap) => {
        const list: BlackoutWithAircraft[] = [];
        snap.forEach((d) =>
          list.push({ id: d.id, aircraftId: a.id, ...(d.data() as BlackoutDoc) })
        );
        byAircraft.set(a.id, list);
        // Flatten into a single array, sorted by startAt.
        const flat = ([] as BlackoutWithAircraft[])
          .concat(...byAircraft.values())
          .sort((x, y) => x.startAt.toMillis() - y.startAt.toMillis());
        setBlackouts(flat);
      });
      unsubs.push(u);
    }
    return () => {
      for (const u of unsubs) u();
    };
  }, [aviones, today]);

  const pending = useMemo(
    () => (rows ?? []).filter((f) => f.status === "pending" && f.fecha >= today),
    [rows, today]
  );
  // "Esta semana" — next 7 days starting today. Drops cancelled +
  // rejected so the panel reads as "what's actually going to fly".
  const weekFlights = useMemo(() => {
    const start = today;
    const t = new Date();
    t.setUTCDate(t.getUTCDate() + 7);
    const end = new Intl.DateTimeFormat("en-CA", {
      timeZone: "America/Santiago",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).format(t);
    return (rows ?? [])
      .filter(
        (f) =>
          f.fecha >= start &&
          f.fecha < end &&
          f.status !== "cancelled" &&
          f.status !== "rejected"
      )
      .sort((a, b) =>
        a.fecha !== b.fecha
          ? a.fecha < b.fecha ? -1 : 1
          : a.hora < b.hora ? -1 : a.hora > b.hora ? 1 : 0
      );
  }, [rows, today]);

  // Only render the pilot section when this admin is themselves a
  // pilot — flips it off for ops admins who never fly. Includes both
  // approved (pilotUid set) and pending requests (pilotUidRequested)
  // so they can spot incoming assignments early.
  const isPilot = !!usuario?.roles.includes("pilot");
  const myPilotFlights = useMemo(() => {
    if (!usuario || !isPilot) return [];
    const me = usuario.id;
    return (rows ?? [])
      .filter((f) => f.fecha >= today
        && f.status !== "cancelled"
        && f.status !== "rejected"
        && (f.pilotUid === me
            || (f.pilotUidRequested === me && !f.pilotUid)));
  }, [rows, today, usuario, isPilot]);

  if (rows === null || !aviones) {
    return <p className="text-sm text-slate-500">Cargando…</p>;
  }

  return (
    <div className="space-y-6">
      <header>
        <div className="mb-2 h-[3px] w-8 rounded-full bg-brand-700" />
        <h1 className="text-2xl font-semibold tracking-tight text-brand-900">Resumen</h1>
        <p className="mt-1 text-sm text-slate-600">
          <span>Próximos vuelos y estado de la flota.</span>
          <span className="block md:ml-1 md:inline">
            <Link to="/admin/flights" className="text-sky-700 hover:text-sky-900">
              Ver calendario completo
            </Link>
            .
          </span>
        </p>
      </header>

      {pending.length > 0 ? <PendingSection flights={pending} /> : null}

      {isPilot && myPilotFlights.length > 0 ? (
        <MyPilotSection flights={myPilotFlights} />
      ) : null}

      <WeekPanel flights={weekFlights} navigate={navigate} today={today} />

      <AircraftStatusGrid
        aircraft={aviones}
        allFlights={rows}
        blackouts={blackouts}
        today={today}
      />
    </div>
  );
}

function WeekPanel({
  flights,
  navigate,
  today,
}: {
  flights: VueloRow[];
  navigate: (to: string) => void;
  today: string;
}) {
  if (flights.length === 0) return null;
  // Group by fecha so the user sees one date header followed by that
  // day's flights — easier to plan than a flat chronological list.
  const groups = new Map<string, VueloRow[]>();
  for (const f of flights) {
    const list = groups.get(f.fecha) ?? [];
    list.push(f);
    groups.set(f.fecha, list);
  }
  return (
    <section className="rounded-lg border border-slate-200 bg-white shadow-sm p-4">
      <div className="flex items-baseline justify-between gap-2">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-700">
          Esta semana
        </h2>
        <span className="font-mono text-xs text-slate-500">
          {flights.length}
        </span>
      </div>
      <ul className="mt-3 space-y-3">
        {Array.from(groups.entries()).map(([fecha, list]) => (
          <li key={fecha}>
            <div className="text-xs font-medium uppercase tracking-wide text-slate-500">
              {formatFecha(fecha)}{" "}
              <span className="text-slate-400">({dowEs(fecha)})</span>
              {fecha === today ? (
                <span className="ml-2 rounded bg-sky-100 px-1.5 py-0.5 text-[10px] font-semibold text-sky-800">
                  hoy
                </span>
              ) : null}
            </div>
            <ul className="mt-1 space-y-1">
              {list.map((f) => (
                <li
                  key={f.id}
                  role="link"
                  tabIndex={0}
                  onClick={() => navigate(`/admin/flights/${f.id}`)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") navigate(`/admin/flights/${f.id}`);
                  }}
                  className="flex cursor-pointer flex-wrap items-baseline gap-x-3 gap-y-1 rounded-md px-2 py-1 text-sm hover:bg-brand-50"
                >
                  <span className="font-mono text-xs text-slate-700">
                    {formatHora(f.hora)}
                  </span>
                  <span className="text-slate-300" aria-hidden>·</span>
                  <span className="text-slate-900">
                    {f.aircraftDisplayName}
                  </span>
                  <span className="text-slate-300" aria-hidden>·</span>
                  <span className="truncate text-slate-700">
                    {f.originDisplay}{" "}
                    <span className="whitespace-nowrap text-slate-500">
                      → {f.destinationDisplay}
                    </span>
                  </span>
                  <span className="text-slate-300" aria-hidden>·</span>
                  <span className="text-xs text-slate-500">
                    {f.requesterDisplayName}
                  </span>
                </li>
              ))}
            </ul>
          </li>
        ))}
      </ul>
    </section>
  );
}

function PendingSection({ flights }: { flights: VueloRow[] }) {
  const navigate = useNavigate();
  return (
    <section className="rounded-lg border-2 border-amber-300 bg-amber-50 p-4">
      <div className="flex items-baseline justify-between gap-2">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-amber-900">
          Solicitudes pendientes
        </h2>
        <span className="rounded-full bg-amber-200 px-2 py-0.5 text-xs font-medium text-amber-900">
          {flights.length}
        </span>
      </div>
      <ul className="mt-3 space-y-2">
        {flights.map((f) => (
          <li
            key={f.id}
            role="link"
            tabIndex={0}
            onClick={() => navigate(`/admin/flights/${f.id}`)}
            onKeyDown={(e) => {
              if (e.key === "Enter") navigate(`/admin/flights/${f.id}`);
            }}
            className="flex cursor-pointer flex-wrap items-baseline justify-between gap-x-3 gap-y-1 rounded-md bg-white px-3 py-2 text-sm shadow-sm hover:bg-amber-100"
          >
            <span className="font-mono text-xs text-slate-700">
              {formatFecha(f.fecha)}{" "}
              <span className="text-slate-400">({dowEs(f.fecha)})</span>{" "}
              · {formatHora(f.hora)}
            </span>
            <span className="flex items-center gap-1.5 text-slate-900">
              {f.aircraftDisplayName}
              {f.modificationRequest ? (
                <span className="rounded bg-amber-200 px-1.5 py-0.5 text-[10px] font-medium uppercase text-amber-900">
                  mod
                </span>
              ) : null}
            </span>
            <span className="truncate text-slate-700">
              {f.originDisplay}{" "}
              <span className="whitespace-nowrap text-slate-500">→ {f.destinationDisplay}</span>
            </span>
            <span className="text-xs text-slate-500">
              {f.requesterDisplayName}
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}

function MyPilotSection({ flights }: { flights: VueloRow[] }) {
  return (
    <section className="rounded-lg border border-sky-200 bg-sky-50/60 p-4">
      <div className="flex items-baseline justify-between gap-2">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-sky-900">
          Mis vuelos como piloto
        </h2>
        <span className="font-mono text-xs text-sky-900/70">
          {flights.length}
        </span>
      </div>
      <ul className="mt-3 space-y-2">
        {flights.map((f) => (
          <li key={f.id}>
            <Link
              to={`/admin/flights/${f.id}`}
              className="block rounded-md border border-sky-100 bg-white px-3 py-2 text-sm hover:bg-sky-50"
            >
              <div className="flex items-baseline justify-between gap-2">
                <span className="font-mono text-xs text-slate-700">
                  {formatFecha(f.fecha)}{" "}
                  <span className="text-slate-400">({dowEs(f.fecha)})</span>
                  {" · "}
                  {formatHora(f.hora)}
                </span>
                <StatusBadge status={f.status} />
              </div>
              <div className="mt-1 text-sm font-medium text-slate-900">
                {f.aircraftDisplayName}
              </div>
              <div className="truncate text-xs text-slate-700">
                {f.originDisplay}{" "}
                <span className="whitespace-nowrap text-slate-500">→ {f.destinationDisplay}</span>
              </div>
              <div className="mt-1 text-[11px] text-slate-600">
                <span className="text-slate-500">Solicita:</span>{" "}
                {f.requesterDisplayName}
                {f.accountCode ? (
                  <>
                    {" · "}
                    <span className="text-slate-500">Cuenta</span>{" "}
                    <span className="font-mono">{f.accountCode}</span>
                  </>
                ) : null}
              </div>
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}

function AircraftStatusGrid({
  aircraft,
  allFlights,
  blackouts,
  today,
}: {
  aircraft: AvionRow[];
  allFlights: VueloRow[];
  blackouts: BlackoutWithAircraft[];
  today: string;
}) {
  const navigate = useNavigate();
  const nowMs = Date.now();
  return (
    <section className="space-y-3">
      <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
        Estado de aviones
      </h2>
      <div className="grid gap-4 sm:grid-cols-2">
        {aircraft.map((a) => {
          // Current location: most recent past flight's post-location;
          // otherwise homeBase.
          const past = allFlights
            .filter(
              (f) =>
                f.aircraftId === a.id &&
                (f.status === "approved" || f.status === "completed") &&
                f.endAt &&
                f.endAt.toMillis() <= nowMs
            )
            .sort((x, y) => y.endAt.toMillis() - x.endAt.toMillis());
          const lastFlight = past[0] ?? null;
          let currentIcao = a.homeBaseIcao;
          let currentSource = "base";
          if (lastFlight) {
            switch (lastFlight.postFlightDisposition) {
              case "home":
                currentIcao = a.homeBaseIcao;
                currentSource = `desde ${formatFecha(lastFlight.fecha)}`;
                break;
              case "relocate_other":
                currentIcao =
                  lastFlight.postFlightDestinationIcao ?? a.homeBaseIcao;
                currentSource = `desde ${formatFecha(lastFlight.fecha)}`;
                break;
              case "stay":
              default:
                currentIcao = lastFlight.destinationIcao;
                currentSource = `desde ${formatFecha(lastFlight.fecha)}`;
            }
          }
          // Next 3 flights: pending/approved with startAt > now,
          // ordered by start. Gives admins a small planning horizon
          // beyond "what's next" — useful for spotting ferry choices.
          const upcomingFlights = allFlights
            .filter(
              (f) =>
                f.aircraftId === a.id &&
                (f.status === "pending" || f.status === "approved") &&
                f.startAt &&
                f.startAt.toMillis() > nowMs
            )
            .sort((x, y) => x.startAt.toMillis() - y.startAt.toMillis())
            .slice(0, 3);
          // Blackouts: split into active (covers now) and upcoming.
          const myBlackouts = blackouts.filter((b) => b.aircraftId === a.id);
          const active = myBlackouts.find(
            (b) => b.startAt.toMillis() <= nowMs && b.endAt.toMillis() >= nowMs
          );
          const upcoming = myBlackouts.find(
            (b) => b.startAt.toMillis() > nowMs
          );
          return (
            <div
              key={a.id}
              role="link"
              tabIndex={0}
              onClick={() => navigate("/admin/aircraft")}
              onKeyDown={(e) => {
                if (e.key === "Enter") navigate("/admin/aircraft");
              }}
              className="flex h-full cursor-pointer flex-col rounded-lg border border-slate-200 bg-white p-4 text-sm shadow-sm transition hover:bg-brand-50 hover:shadow-md"
            >
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-4">
                  <h3 className="text-base font-semibold text-slate-900">
                    {a.displayName}
                  </h3>
                  {a.id === "cirrus" || a.id === "pilatus" ? (
                    <img
                      src={`/${a.id}.png`}
                      alt=""
                      loading="lazy"
                      className="pointer-events-none h-[84px] w-auto select-none"
                    />
                  ) : null}
                </div>
                {a.tailNumber ? (
                  <span className="font-mono text-xs text-slate-500">
                    {a.tailNumber}
                  </span>
                ) : null}
              </div>
              <dl className="mt-1 space-y-1 text-sm">
                <div className="flex items-baseline justify-between gap-2">
                  <dt className="text-slate-500">Ubicación</dt>
                  <dd className="text-right">
                    <span className="font-mono text-slate-900">
                      {currentIcao.replace(/^_/, "")}
                    </span>
                    <span className="ml-2 text-[11px] text-slate-500">
                      {currentSource}
                    </span>
                  </dd>
                </div>
                <div>
                  <dt className="text-slate-500">Próximos vuelos</dt>
                  <dd className="mt-1 space-y-1">
                    {upcomingFlights.length === 0 ? (
                      <span className="text-slate-500">—</span>
                    ) : (
                      upcomingFlights.map((u) => (
                        <Link
                          key={u.id}
                          to={`/admin/flights/${u.id}`}
                          onClick={(e) => e.stopPropagation()}
                          className="block rounded px-1 -mx-1 hover:bg-brand-50"
                        >
                          <span className="font-mono text-xs text-slate-700">
                            {formatFecha(u.fecha)} · {formatHora(u.hora)}
                          </span>
                          <span className="ml-2 text-xs text-slate-700">
                            {u.originIcao.replace(/^_/, "")} →{" "}
                            {u.destinationIcao.replace(/^_/, "")}
                          </span>
                          {u.status === "pending" ? (
                            <span className="ml-1 text-[10px] uppercase text-amber-700">
                              pending
                            </span>
                          ) : null}
                        </Link>
                      ))
                    )}
                  </dd>
                </div>
              </dl>
              {/* mt-auto pushes the blackout block to the bottom of
                  the flex-column card so all cards in the row align
                  their blackouts on the same baseline. */}
              {active ? (
                <div className="mt-auto pt-3">
                  <div className="rounded-md border-2 border-orange-400 bg-orange-50 p-2 text-xs">
                    <div className="font-medium text-orange-900">
                      Bloqueo activo
                    </div>
                    <div className="text-slate-700">
                      {formatFecha(tsToIso(active.startAt))} →{" "}
                      {formatFecha(tsToIso(active.endAt))}
                    </div>
                    {active.reason ? (
                      <div className="text-slate-600">{active.reason}</div>
                    ) : null}
                  </div>
                </div>
              ) : upcoming ? (
                <div className="mt-auto pt-3">
                  <div className="rounded-md border border-orange-300 bg-orange-50/60 p-2 text-xs">
                    <div className="font-medium text-orange-900">
                      Próximo bloqueo
                    </div>
                    <div className="text-slate-700">
                      {formatFecha(tsToIso(upcoming.startAt))} →{" "}
                      {formatFecha(tsToIso(upcoming.endAt))}
                    </div>
                    {upcoming.reason ? (
                      <div className="text-slate-600">{upcoming.reason}</div>
                    ) : null}
                  </div>
                </div>
              ) : null}
              {!a.active ? (
                <p className="mt-3 text-xs italic text-slate-500">
                  Avión marcado como inactivo en la flota.
                </p>
              ) : null}
              {today /* keep reference so daily lookups recompute */ === "" ? null : null}
            </div>
          );
        })}
      </div>
    </section>
  );
}

function StatusBadge({ status }: { status: string }) {
  const cls: Record<string, string> = {
    approved: "border border-emerald-200 bg-emerald-50 text-emerald-800",
    pending: "border border-amber-200 bg-amber-50 text-amber-800",
    rejected: "border border-rose-200 bg-rose-50 text-rose-800",
    cancelled: "border border-rose-200 bg-rose-50 text-rose-800",
    completed: "border border-slate-200 bg-slate-50 text-slate-600",
  };
  return (
    <span
      className={`inline-block rounded px-1.5 py-0.5 text-[10px] font-medium ${
        cls[status] ?? "bg-slate-100 text-slate-700"
      }`}
    >
      {status}
    </span>
  );
}

function tsToIso(t: Timestamp): string {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/Santiago",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(t.toDate());
}
